import {
    Backdrop,
    CircularProgress,
    Typography,
    Box,
    Paper,
    Card,
    CardContent,
    CardActionArea
} from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import usePageTitle from "../../../Common/hooks/usePageTitle";
import TopBar from "../../../Layout/components/TopBar/TopBar";
import { useReportDetail } from "../hooks/useReportDetail";
import ReportDetailsGrid from "../components/ReportDetailsGrid";
import styles from "./ReportDetailView.module.scss";

interface SummaryCardProps {
    title: string;
    value: number;
    color?: string;
    subtitle?: string;
    actionFilter: string;
    isSelected: boolean;
    onClick: (action: string) => void;
}

const SummaryCard = ({ title, value, color, subtitle, actionFilter, isSelected, onClick }: SummaryCardProps): JSX.Element => (
    <Card 
        sx={{ 
            height: "100%", 
            display: "flex", 
            border: isSelected ? "2px solid" : "1px solid transparent",
            borderColor: isSelected ? "primary.main" : "transparent"
        }}
    >
        <CardActionArea 
            onClick={() => onClick(actionFilter)} 
            sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "stretch" }}
        >
            <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <Typography color="textSecondary" gutterBottom>
                    {title}
                </Typography>
                <Typography variant="h4" color={color || "textPrimary"}>
                    {value}
                </Typography>
                {subtitle && (
                    <Typography variant="caption">
                        {subtitle}
                    </Typography>
                )}
            </CardContent>
        </CardActionArea>
    </Card>
);

const ReportDetailView = (): JSX.Element => {
    const { id } = useParams<{ id: string }>();
    const { reportDetail, isLoading, fetchReportDetail } = useReportDetail();
    const [selectedAction, setSelectedAction] = useState<string>("");
    usePageTitle("Admin | Report Details");

    useEffect(() => {
        if (id) {
            fetchReportDetail(Number.parseInt(id, 10));
        }
    }, [id]);

    const handleCardClick = (action: string) => {
        setSelectedAction(action === selectedAction ? "" : action);
    };

    if (!reportDetail && !isLoading) {
        return <div>Report not found</div>;
    }

    return (
        <div id="reportdetail" data-testid="reportdetail" style={{ height: "100%" }}>
            <TopBar>
                <div className={styles.details__topBarContainer}>
                    <Typography variant="h4">
                        Cost Center Validation Report Details
                    </Typography>
                </div>
            </TopBar>

            <Backdrop
                sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={isLoading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            {reportDetail && (
                <Box pl="80px" pt="24px" height="100%">
                    <Paper sx={{ width: "92%", p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Report Summary - {new Date(reportDetail.completedDate).toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            Click a card to filter the details below
                        </Typography>
                        <Box 
                            display="flex" 
                            flexWrap="wrap" 
                            gap={2} 
                            sx={{ 
                                "& > *": { 
                                    flex: "1 1 200px", 
                                    minWidth: "150px", 
                                    maxWidth: "250px" 
                                } 
                            }}
                        >
                            <SummaryCard
                                title="Total Processed"
                                value={reportDetail.totalProjectsProcessed}
                                subtitle={`Flagged: ${reportDetail.flaggedProjectsCount} | To Validate: ${reportDetail.projectsToValidateCount}`}
                                actionFilter=""
                                isSelected={selectedAction === ""}
                                onClick={handleCardClick}
                            />
                            <SummaryCard
                                title="Valid"
                                value={reportDetail.validCount}
                                color="success.main"
                                actionFilter="Valid"
                                isSelected={selectedAction === "Valid"}
                                onClick={handleCardClick}
                            />
                            <SummaryCard
                                title="Locked"
                                value={reportDetail.lockedCount}
                                color="error"
                                actionFilter="Locked"
                                isSelected={selectedAction === "Locked"}
                                onClick={handleCardClick}
                            />
                            <SummaryCard
                                title="Advanced"
                                value={reportDetail.advancedCount}
                                color="warning.main"
                                actionFilter="FlagAdvanced"
                                isSelected={selectedAction === "FlagAdvanced"}
                                onClick={handleCardClick}
                            />
                            <SummaryCard
                                title="Newly Flagged"
                                value={reportDetail.newlyFlaggedCount}
                                color="warning.main"
                                actionFilter="NewlyFlagged"
                                isSelected={selectedAction === "NewlyFlagged"}
                                onClick={handleCardClick}
                            />
                            <SummaryCard
                                title="Unflagged"
                                value={reportDetail.unflaggedCount}
                                color="success.main"
                                actionFilter="Unflagged"
                                isSelected={selectedAction === "Unflagged"}
                                onClick={handleCardClick}
                            />
                        </Box>
                    </Paper>

                    <ReportDetailsGrid 
                        details={reportDetail.processingDetails} 
                        initialActionFilter={selectedAction}
                    />
                </Box>
            )}
        </div>
    );
};

export default ReportDetailView;
